import { CameraIcon } from '@radix-ui/react-icons';
import { PhotoCarouselAdminView } from './adminViewPreference';
import {
  PhotoSubmissionFilter,
  PhotoSubmissionModel,
  PhotoSubmissionStatus,
} from './submissionStatus';
import {
  PhotoCarouselStatusTabs,
  PhotoCarouselViewToggle,
} from './SubmissionsToolbar';
import { PhotoPreviewDialog, PhotoSubmissionLayout } from './PhotoSubmission';

export function PhotoSubmissionsPanel({
  view,
  onViewChange,
  filter,
  onFilterChange,
  counts,
  submissions,
  bansEnabled,
  requireApproval,
  newIds,
  previewId,
  onPreviewIdChange,
  moreMenuId,
  onMoreMenuIdChange,
  onApprove,
  onReject,
  onRequestDelete,
  onBan,
  onInteract,
}: {
  view: PhotoCarouselAdminView;
  onViewChange: (view: PhotoCarouselAdminView) => void;
  filter: PhotoSubmissionFilter;
  onFilterChange: (filter: PhotoSubmissionFilter) => void;
  counts: Record<PhotoSubmissionStatus, number> & { all: number };
  submissions: PhotoSubmissionModel[];
  bansEnabled: boolean;
  requireApproval: boolean;
  newIds?: Set<string>;
  previewId: string | null;
  onPreviewIdChange: (id: string | null) => void;
  moreMenuId?: string | null;
  onMoreMenuIdChange?: (id: string | null) => void;
  onApprove: (submission: PhotoSubmissionModel) => void;
  onReject: (submission: PhotoSubmissionModel) => void;
  onRequestDelete: (submission: PhotoSubmissionModel) => void;
  onBan: (submission: PhotoSubmissionModel) => void;
  onInteract?: (id: string) => void;
}) {
  const emptyAll = counts.all === 0;
  const emptyFilter = submissions.length === 0;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-end justify-between gap-3 border-b border-border">
        <PhotoCarouselStatusTabs
          value={filter}
          onChange={onFilterChange}
          counts={counts}
        />
        <div className="hidden shrink-0 pb-1.5 lg:block">
          <PhotoCarouselViewToggle value={view} onChange={onViewChange} />
        </div>
      </div>

      {emptyAll || emptyFilter ? (
        <EmptySubmissions filter={filter} emptyAll={emptyAll} />
      ) : view === 'list' ? (
        <div
          data-name="PHOTO_SUBMISSION_LIST"
          className="overflow-hidden rounded-[10px] border border-border bg-card"
        >
          <div className="hidden h-9 items-center gap-4 border-b border-border px-4 text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground lg:grid lg:grid-cols-[64px_minmax(240px,1fr)_96px_120px_328px]">
            <span />
            <span>Submitted by</span>
            <span>Time</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>
          {submissions.map((submission) => (
            <PhotoSubmissionLayout
              key={submission.id}
              variant="list"
              submission={submission}
              requireApproval={requireApproval}
              bansEnabled={bansEnabled}
              isNew={newIds?.has(submission.id)}
              moreMenuOpen={moreMenuId === submission.id}
              onMoreMenuOpenChange={(open) =>
                onMoreMenuIdChange?.(open ? submission.id : null)
              }
              onApprove={() => onApprove(submission)}
              onReject={() => onReject(submission)}
              onRequestDelete={() => onRequestDelete(submission)}
              onPreview={() => onPreviewIdChange(submission.id)}
              onBan={() => onBan(submission)}
              onInteract={() => onInteract?.(submission.id)}
            />
          ))}
        </div>
      ) : (
        <div
          data-name="PHOTO_SUBMISSION_CARDS"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
        >
          {submissions.map((submission) => (
            <PhotoSubmissionLayout
              key={submission.id}
              variant="cards"
              submission={submission}
              requireApproval={requireApproval}
              bansEnabled={bansEnabled}
              moreMenuOpen={moreMenuId === submission.id}
              onMoreMenuOpenChange={(open) =>
                onMoreMenuIdChange?.(open ? submission.id : null)
              }
              onApprove={() => onApprove(submission)}
              onReject={() => onReject(submission)}
              onRequestDelete={() => onRequestDelete(submission)}
              onPreview={() => onPreviewIdChange(submission.id)}
              onBan={() => onBan(submission)}
              onInteract={() => onInteract?.(submission.id)}
            />
          ))}
        </div>
      )}

      {!emptyFilter && view === 'list' && (
        <p className="text-xs text-muted-foreground">
          Newest first · new submissions appear at the top
        </p>
      )}

      <PhotoPreviewDialog
        open={Boolean(previewId)}
        onOpenChange={(open) => {
          if (!open) onPreviewIdChange(null);
        }}
        submissions={submissions}
        currentId={previewId}
        onCurrentIdChange={onPreviewIdChange}
        bansEnabled={bansEnabled}
        onApprove={onApprove}
        onReject={onReject}
        onBan={onBan}
      />
    </div>
  );
}

function EmptySubmissions({
  filter,
  emptyAll,
}: {
  filter: PhotoSubmissionFilter;
  emptyAll: boolean;
}) {
  const title = emptyAll
    ? 'No submissions yet'
    : filter === 'pending'
      ? 'No pending submissions'
      : filter === 'approved'
        ? 'No approved submissions'
        : 'No rejected submissions';
  const body = emptyAll
    ? 'Photos from your audience show up here as they come in.'
    : 'Nothing to review right now.';
  return (
    <div
      data-name="SUBMISSIONS_EMPTY"
      className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-16 text-center"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <CameraIcon className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
